import { stepsForBeat, type Subdivision } from '../model/types';
/**
 * Ableitung der rhythmischen Notation einer Stimme aus den Anschlägen
 * im Sechzehntelraster.
 *
 * Vorgehen je Zählzeit (vier Sechzehntel-Slots):
 * - Führende Pause bis zum ersten Anschlag der Zählzeit.
 * - Jeder Anschlag erhält als Dauer den Abstand bis zum nächsten
 *   Anschlag innerhalb der Zählzeit (bzw. bis zum Zählzeitende). Dadurch
 *   entstehen keine internen Pausen; Zählzeiten werden nicht überbunden.
 * - Enthält eine Zählzeit mindestens zwei Noten, werden diese gebalkt
 *   (gemeinsame beamId); eine einzelne Note erhält ein Fähnchen.
 *
 * Rein funktional, ohne DOM- oder SVG-Bezug.
 */

import {
  BEATS_PER_BAR,
  STEPS_PER_BEAT,
  type NoteValue,
  type RestValue,
} from './types';

export interface RhythmNote {
  readonly kind: 'note';
  /** Absoluter Schrittindex (0..15) des Anschlags. */
  readonly step: number;
  readonly value: NoteValue;
  /** Gemeinsame Balkengruppe oder `null`, wenn die Note ein Fähnchen trägt. */
  readonly beamId: number | null;
}

export interface RhythmRest {
  readonly kind: 'rest';
  /** Absoluter Schrittindex, an dem die Pause beginnt. */
  readonly step: number;
  readonly value: RestValue;
}

export type RhythmEvent = RhythmNote | RhythmRest;

function noteValueForDuration(sixteenths: number): NoteValue {
  switch (sixteenths) {
    case 1:
      return 'sixteenth';
    case 2:
      return 'eighth';
    case 3:
      return 'dotted-eighth';
    case 4:
      return 'quarter';
    default:
      throw new RangeError(`Ungueltige Notendauer: ${sixteenths}`);
  }
}

/**
 * Zerlegt eine führende Pause der Länge `sixteenths` (1..3) am Beginn
 * einer Zählzeit in standardkonforme Pausenwerte.
 */
function leadingRestPieces(sixteenths: number): RestValue[] {
  switch (sixteenths) {
    case 1:
      return ['sixteenth'];
    case 2:
      return ['eighth'];
    case 3:
      return ['eighth', 'sixteenth'];
    default:
      throw new RangeError(`Ungueltige Pausendauer: ${sixteenths}`);
  }
}

const REST_VALUE_SIXTEENTHS: Readonly<Record<RestValue, number>> = {
  sixteenth: 1,
  eighth: 2,
  quarter: 4,
};

/**
 * Erzeugt die Rhythmusereignisse einer Stimme für einen ganzen Takt.
 *
 * @param onsets Menge der Schrittindizes (0..15) mit einem Anschlag.
 */
export function voiceRhythm(
  onsets: ReadonlySet<number>,
  subdivisions: readonly Subdivision[] = [4, 4, 4, 4],
): RhythmEvent[] {
  const events: RhythmEvent[] = [];
  let nextBeamId = 0;

  for (let beat = 0; beat < BEATS_PER_BAR; beat += 1) {
    const base = beat * STEPS_PER_BEAT;
    const subdivision = subdivisions[beat]!;
    if (subdivision === 3 || subdivision === 6) {
      const steps = stepsForBeat(beat, subdivision);
      if (!steps.some((step) => onsets.has(step))) {
        events.push({ kind: 'rest', step: base, value: 'quarter' });
        continue;
      }
      const value = subdivision === 3 ? 'eighth' : 'sixteenth';
      for (let group = 0; group < subdivision / 3; group++) {
        const groupSteps = steps.slice(group * 3, group * 3 + 3);
        const beamed =
          groupSteps.filter((step) => onsets.has(step)).length >= 2;
        const beamId = beamed ? nextBeamId++ : null;
        for (const step of groupSteps) {
          events.push(
            onsets.has(step)
              ? { kind: 'note', step, value, beamId }
              : { kind: 'rest', step, value },
          );
        }
      }
      continue;
    }
    const slots: number[] = [];
    for (let slot = 0; slot < STEPS_PER_BEAT; slot += 1) {
      if (onsets.has(base + slot)) {
        slots.push(slot);
      }
    }

    if (slots.length === 0) {
      events.push({ kind: 'rest', step: base, value: 'quarter' });
      continue;
    }

    const firstSlot = slots[0] as number;
    if (firstSlot > 0) {
      let restStep = base;
      for (const value of leadingRestPieces(firstSlot)) {
        events.push({ kind: 'rest', step: restStep, value });
        restStep += REST_VALUE_SIXTEENTHS[value];
      }
    }

    const beamed = slots.length >= 2;
    const beamId = beamed ? nextBeamId : null;
    if (beamed) {
      nextBeamId += 1;
    }

    for (let i = 0; i < slots.length; i += 1) {
      const slot = slots[i] as number;
      const nextSlot =
        i + 1 < slots.length ? (slots[i + 1] as number) : STEPS_PER_BEAT;
      events.push({
        kind: 'note',
        step: base + slot,
        value: noteValueForDuration(nextSlot - slot),
        beamId,
      });
    }
  }

  return events;
}
